import type { Location } from 'history'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

interface BlockNavigationResult {
  isBlocked: boolean
  confirmNavigation: () => void
  cancelNavigation: () => void
}

interface UseBlockNavigationOptions {
  shouldBlockLocation?: (nextLocation: Location) => boolean
  navigate?: (location: Location) => void
}

export function useBlockNavigation(
  shouldBlock: boolean,
  { shouldBlockLocation, navigate }: UseBlockNavigationOptions = {},
): BlockNavigationResult {
  const history = useHistory()
  const [blockedLocation, setBlockedLocation] = useState<Location | null>(null)
  const isConfirmingNavigationRef = useRef(false)
  const shouldBlockLocationRef = useRef(shouldBlockLocation)
  const navigateRef = useRef(navigate)
  shouldBlockLocationRef.current = shouldBlockLocation
  navigateRef.current = navigate

  useEffect(() => {
    if (!shouldBlock) {
      setBlockedLocation(null)
      return undefined
    }

    return history.block(nextLocation => {
      if (isConfirmingNavigationRef.current) return undefined
      if (shouldBlockLocationRef.current && !shouldBlockLocationRef.current(nextLocation)) return undefined
      setBlockedLocation(nextLocation)
      return false
    })
  }, [history, shouldBlock])

  useEffect(() => {
    if (!shouldBlock) return undefined

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }

    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [shouldBlock])

  const confirmNavigation = () => {
    if (!blockedLocation) return

    isConfirmingNavigationRef.current = true
    if (navigateRef.current) {
      navigateRef.current(blockedLocation)
    } else {
      history.push(blockedLocation)
    }
    isConfirmingNavigationRef.current = false
    setBlockedLocation(null)
  }

  const cancelNavigation = () => setBlockedLocation(null)

  return {
    isBlocked: Boolean(blockedLocation),
    confirmNavigation,
    cancelNavigation,
  }
}
